import { useEffect, type RefObject } from "react";
import { isTopmostPlatformLayer } from "./useDismissibleLayer";

let scrollLockCount = 0;
let previousBodyOverflow = "";
let previousRootOverflow = "";

function acquireScrollLock() {
  if (typeof document === "undefined") return;
  if (scrollLockCount === 0) {
    previousBodyOverflow = document.body.style.overflow;
    previousRootOverflow = document.documentElement.style.overflow;
    // Clip does not create a new scroll container for sticky workspace navigation.
    document.body.style.overflow = "clip";
    document.documentElement.style.overflow = "hidden";
  }
  scrollLockCount += 1;
}

function releaseScrollLock() {
  if (typeof document === "undefined" || scrollLockCount === 0) return;
  scrollLockCount -= 1;
  if (scrollLockCount === 0) {
    document.body.style.overflow = previousBodyOverflow;
    document.documentElement.style.overflow = previousRootOverflow;
  }
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function focusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => element.getAttribute("aria-hidden") !== "true"
      && element.getAttribute("aria-disabled") !== "true"
      && !element.hasAttribute("inert"));
}

function canReceiveInitialFocus(element: HTMLElement | null | undefined): element is HTMLElement {
  if (!element || element.getAttribute("aria-hidden") === "true" || element.getAttribute("aria-disabled") === "true") return false;
  return !(element instanceof HTMLButtonElement || element instanceof HTMLInputElement
    || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement) || !element.disabled;
}

type ModalFocusTrap = {
  id: symbol;
  containerRef: RefObject<HTMLElement | null>;
  initialFocusRef?: RefObject<HTMLElement | null>;
  layerRootRef?: RefObject<HTMLElement | null>;
};

const modalFocusStack: ModalFocusTrap[] = [];
let modalFocusListenersInstalled = false;

function topActiveModalTrap(): ModalFocusTrap | undefined {
  for (let index = modalFocusStack.length - 1; index >= 0; index -= 1) {
    const trap = modalFocusStack[index];
    if (!trap) continue;
    if (!trap.layerRootRef || isTopmostPlatformLayer(trap.layerRootRef.current)) return trap;
  }
  return undefined;
}

function focusBoundary(trap: ModalFocusTrap, preferLast = false): void {
  const container = trap.containerRef.current;
  if (!container) return;
  const focusable = focusableElements(container);
  const target = preferLast ? focusable[focusable.length - 1] : focusable[0];
  (target ?? container).focus();
}

function handleModalDocumentKeyDown(event: KeyboardEvent) {
  if (event.key !== "Tab") return;
  const trap = topActiveModalTrap();
  const container = trap?.containerRef.current;
  if (!trap || !container) return;

  const focusable = focusableElements(container);
  const active = document.activeElement;
  if (!(active instanceof Node) || !container.contains(active)) {
    event.preventDefault();
    focusBoundary(trap, event.shiftKey);
    return;
  }

  if (focusable.length === 0) {
    event.preventDefault();
    container.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) return;
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

function handleModalDocumentFocusIn(event: FocusEvent) {
  const trap = topActiveModalTrap();
  const container = trap?.containerRef.current;
  if (!trap || !container || !(event.target instanceof Node) || container.contains(event.target)) return;
  focusBoundary(trap);
}

function installModalFocusListeners() {
  if (modalFocusListenersInstalled || typeof document === "undefined") return;
  document.addEventListener("keydown", handleModalDocumentKeyDown, true);
  document.addEventListener("focusin", handleModalDocumentFocusIn, true);
  modalFocusListenersInstalled = true;
}

function removeModalFocusListenersWhenIdle() {
  if (!modalFocusListenersInstalled || modalFocusStack.length > 0 || typeof document === "undefined") return;
  document.removeEventListener("keydown", handleModalDocumentKeyDown, true);
  document.removeEventListener("focusin", handleModalDocumentFocusIn, true);
  modalFocusListenersInstalled = false;
}

/** Locks background scrolling only while a genuinely modal layer is open. */
export function useModalScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    acquireScrollLock();
    return releaseScrollLock;
  }, [active]);
}

/**
 * Keeps keyboard focus inside the topmost modal layer without affecting
 * ordinary drawers or popovers. Document-level guards recover focus even when
 * an async/dynamic render removes the element that previously held focus.
 */
export function useModalFocusTrap(
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
  initialFocusRef?: RefObject<HTMLElement | null>,
  layerRootRef?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const id = Symbol("kavoosh-modal-focus-trap");
    const trap: ModalFocusTrap = {
      id,
      containerRef,
      ...(initialFocusRef ? { initialFocusRef } : {}),
      ...(layerRootRef ? { layerRootRef } : {}),
    };
    modalFocusStack.push(trap);
    installModalFocusListeners();

    const focusInitial = () => {
      if (topActiveModalTrap()?.id !== id) return;
      const preferred = initialFocusRef?.current;
      const target = canReceiveInitialFocus(preferred) ? preferred : (focusableElements(container)[0] ?? container);
      target.focus({ preventScroll: true });
    };
    const frame = typeof window !== "undefined" && typeof window.requestAnimationFrame === "function"
      ? window.requestAnimationFrame(focusInitial)
      : null;
    if (frame === null) focusInitial();

    return () => {
      if (frame !== null && typeof window !== "undefined") window.cancelAnimationFrame(frame);
      const index = modalFocusStack.findIndex((candidate) => candidate.id === id);
      if (index >= 0) modalFocusStack.splice(index, 1);
      removeModalFocusListenersWhenIdle();
    };
  }, [active, containerRef, initialFocusRef, layerRootRef]);
}

export function useModalBehavior(
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
  initialFocusRef?: RefObject<HTMLElement | null>,
  layerRootRef?: RefObject<HTMLElement | null>,
): void {
  useModalScrollLock(active);
  useModalFocusTrap(active, containerRef, initialFocusRef, layerRootRef);
}
